import React, { useEffect, useState } from 'react';
import PageHeaderText from 'components/base/page-header-text';
import { Link } from 'react-router-dom';
import FeedCard from 'components/feed-card';
import { ROUTE_KEYS } from 'utils/constants';
import PageText from 'components/base/page-text';
import { apiService } from 'services/base.service';
import { CokitchenExploreKeywordType } from 'utils/types';
import DeleteModal from 'components/base/delete-modal';
import { toast } from 'react-toastify';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { AddIcon } from 'cokitchen-icons';
import useDashboardContext from 'contexts/dashboard.context';
import { ReactComponent as ArchiveIcon } from 'assets/icons/archive-icon.svg';
import AccessDenied from 'components/access-denied';

const FeedsList = () => {
  const { category, activeCokitchen, permissions } = useDashboardContext();
  const [loading, setLoading] = useState(false);
  const [feeds, setFeeds] = useState<CokitchenExploreKeywordType[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeFeed, setActiveFeed] = useState<CokitchenExploreKeywordType>();

  const getFeeds = async () => {
    try {
      setLoading(true);
      const res = await apiService.post('cokitchen-explore-keyword-new', {
        data: {
          item_type: category?.toUpperCase(),
          cokitchen_id: activeCokitchen.id
        },
        isInternal: true
      });
      if (res?.data) {
        setFeeds(res?.data?.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const handleDeleteClick = async () => {
    setLoading(true);
    try {
      const res = await apiService.delete(`marketing/cokitchen-explore-keyword/${activeFeed?.id}`);
      if (res?.data) {
        toast(<ToastMessage type='success' message='Feed deleted successfully' />);
        getFeeds();
        setShowDeleteModal(false);
      }
    } catch {
      toast(<ToastMessage type='error' message='Error occured while deleting feed' />);
    }
    setLoading(false);
  };

  useEffect(() => {
    getFeeds();
  }, [category]);

  if (!permissions.canAccessFeeds) return <AccessDenied />;
  return (
    <div>
      <PageHeaderText
        title='Manage items on the explore feed'
        description='Help users easily find meals on the FoodCourt App'>
        <Link to={ROUTE_KEYS.NEW_FEED}>
          <Button
            variant='link'
            color='primary'
            className='bg-primary text-red px-6 py-2 text-sm font-semibold'>
            <AddIcon className='w-5 h-5' />
            Add new
          </Button>
        </Link>
      </PageHeaderText>
      {loading ? (
        <PageText text='Loading...' />
      ) : feeds?.length === 0 ? (
        <PageText text='No Feed To Show' />
      ) : (
        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'>
          {feeds
            ?.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
            ?.map((feed) => (
              <FeedCard
                key={feed.id}
                image={feed?.meal_keyword?.images?.[0] || ''}
                actions={
                  <div className='flex gap-2 absolute top-2 right-2'>
                    <ArchiveIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFeed(feed);
                        setShowDeleteModal(true);
                      }}
                      className={` w-7 h-7 text-white bg-black hover:bg-black bg-opacity-70 rounded-md p-[7.5px] transition-none z-10`}
                    />
                  </div>
                }
                className='mb-2'>
                <div className='flex justify-between items-center mt-2'>
                  <div>
                    <p className='text-sm'>{feed?.meal_keyword?.name}</p>
                  </div>
                </div>
              </FeedCard>
            ))}
          <DeleteModal
            show={showDeleteModal}
            onClose={toggleDeleteModal}
            handleDelete={handleDeleteClick}
            title={`Are you sure you want to delete ${activeFeed?.meal_keyword?.name} ?`}
            description='This action cannot be undone'
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default FeedsList;
